import { NotificationService } from '@/services'
import QuoteRoutes from '@/views/quote/route-types'
import { NotificationEntityType, NotificationStatusType, NotificationType } from '@/enums'

const MILLISECONDS_PER_MINUTES = 1000 * 60
const POLLING_INTERVAL = 0.5 * MILLISECONDS_PER_MINUTES

const state = {
  notifications: [],
  count: 0,
  intervalTimer: null
}

const getters = {
  notifications: (state) => state.notifications,
  count: (state) => state.notifications.length
}

const actions = {
  async getNotifications({ commit, dispatch }) {
    const isUnauthorized = localStorage.getItem('isUnauthorized')
    if (isUnauthorized === 'true') {
      return
    }
    try {
      const notifications = await NotificationService.getNotifications(NotificationStatusType.Unread)
      notifications.forEach(notification => {
        const types = [
          NotificationType.QuoteSmsIncoming,
          NotificationType.QuoteWhatsAppIncoming,
          NotificationType.QuoteEmailIncoming
        ]
        if (notification.entityType === NotificationEntityType.Quote && types.includes(notification.type)) {
          notification.route = {
            name: QuoteRoutes.QuoteCommunications.name,
            params: { quoteId: notification.entityId, type: notification.type }
          }
        }
      })
      // Sort newest-first by createdDate as a safety net — keeps the bell-menu
      // ordering correct even if the API hasn't been rebuilt with the ORDER BY.
      notifications.sort((a, b) => {
        const aTime = a.createdDate ? new Date(a.createdDate).getTime() : 0
        const bTime = b.createdDate ? new Date(b.createdDate).getTime() : 0
        return bTime - aTime
      })
      commit('setNotifications', notifications)
    } catch (error) {
      const status = error?.response?.status

      if (status === 401) {
        localStorage.setItem('isSessionExpired', true)
        dispatch('stopNotificationPoll')
      } else {
        throw error
      }
    }
  },
  startNotificationPoll({ commit, state, dispatch }) {
    if (!state.intervalTimer) {
      dispatch('getNotifications')
      commit('setIntervalTimer', () => { dispatch('getNotifications') })
    }
  },
  stopNotificationPoll({ commit, state }) {
    if (state.intervalTimer) {
      commit('clearIntervalTimer')
    }
  },
  // Optimistic dismiss: drop the row from local state immediately, then fire
  // the server call. On failure we restore the snapshot and refetch so the UI
  // stays consistent with the backend.
  async dismissNotification({ commit, state, dispatch }, { id, type }) {
    const snapshot = [...state.notifications]
    commit('removeNotification', id)
    try {
      await NotificationService.setNotificationStatus(id, type, NotificationStatusType.Read)
    } catch (error) {
      commit('setNotifications', snapshot)
      dispatch('getNotifications')
      throw error
    }
  },
  async dismissAllNotifications({ commit, state, dispatch }) {
    const snapshot = [...state.notifications]
    commit('removeDismissableNotifications')
    try {
      await NotificationService.dismissAll()
    } catch (error) {
      commit('setNotifications', snapshot)
      dispatch('getNotifications')
      throw error
    }
  }
}

const mutations = {
  setIntervalTimer(state, callback) {
    if (state.intervalTimer) {
      clearInterval(state.intervalTimer)
    }
    state.intervalTimer = setInterval(() => {
      if (callback) {
        callback()
      }
    }, POLLING_INTERVAL)
  },
  clearIntervalTimer(state) {
    if (state.intervalTimer) {
      clearInterval(state.intervalTimer)
      state.intervalTimer = null
    }
  },
  setNotifications(state, notifications) {
    state.notifications = notifications
  },
  removeNotification(state, id) {
    state.notifications = state.notifications.filter(n => n.id !== id)
  },
  // Mirrors the server-side DismissAll behaviour: drop everything except
  // CompanyLicense, which is persistent and not user-dismissable.
  removeDismissableNotifications(state) {
    state.notifications = state.notifications.filter(
      n => n.type === NotificationType.CompanyLicense
    )
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
